// SeasonView — High-Contrast HUD with glassmorphism phase cards and micro-widgets.
// States: LEARNING · AUTO_READY (preflight → building → review) · GENERATING · READY (drill-down)

import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useAuth } from '@/context/AuthContext';
import {
  fetchSeasonSummary,
  sendCoachChat,
  type SeasonSummaryResponse,
  type SeasonSummary,
  type BaselinePlanArtifact,
  type CoachChatResponse,
} from '@/lib/api';
import {
  Loader2,
  AlertTriangle,
  CalendarPlus,
  Shield,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PlanBuildProgressCard } from '@/components/coach/PlanBuildProgressCard';
import {
  PlanBuildStage,
  type PlanBuildStageValue,
  type PlanBuildProgress,
} from '@/types/planBuildProgress';
import { SeasonPreflightDialog } from '@/components/plan/SeasonPreflightDialog';
import { SeasonPlanReview } from '@/components/plan/SeasonPlanReview';
import { SeasonLoadChart } from '@/components/plan/SeasonLoadChart';
import { RunnerProcessingIndicator } from '@/components/coach/RunnerProcessingIndicator';
import { SeasonSummaryCard } from '@/components/coach/SeasonSummaryCard';
import { usePlanInstances } from '@/hooks/usePlanInstances';
import { trackFirstPlanGenerated } from '@/lib/posthog';
import { log } from '@/lib/logger';

// ─── Style constants ────────────────────────────────────────────────────────
const TEAL = '0, 255, 200';
const AMBER = '255, 180, 60';
const GREY_TEXT = '#A0A0A0';
const WHITE_TEXT = '#E8E8E8';
const CARD_BG = 'rgba(12, 16, 24, 0.9)';
const BORDER_INACTIVE = 'rgba(255, 255, 255, 0.12)';

// ─── Simulated build stages (in-flight animation) ────────────────────────────
const BUILD_STAGES: PlanBuildStageValue[] = [
  PlanBuildStage.ANALYZE_RECENT_TRAINING,
  PlanBuildStage.ANALYZE_TRAINING_LOAD,
  PlanBuildStage.ANALYZE_GOALS_AND_PLAN,
  PlanBuildStage.GENERATE_TRAINING_PLAN,
];

// ─── Types ───────────────────────────────────────────────────────────────────
type LocalPhase = 'idle' | 'preflight' | 'building' | 'review';

interface SeasonViewProps {
  currentDate: Date;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function isFullNarrative(
  narrative: SeasonSummary | BaselinePlanArtifact | null
): narrative is SeasonSummary {
  return (
    narrative !== null &&
    'phases' in narrative &&
    Array.isArray((narrative as SeasonSummary).phases)
  );
}

// ─── Micro load bar ──────────────────────────────────────────────────────────
function LoadBar({
  value,
  max = 100,
  label,
}: {
  value: number;
  max?: number;
  label: string;
}) {
  const pct = Math.min((value / max) * 100, 100);
  const color =
    pct > 70
      ? `rgba(${TEAL}, 0.6)`
      : pct > 40
        ? `rgba(${TEAL}, 0.35)`
        : `rgba(${AMBER}, 0.3)`;
  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono text-[9px] uppercase tracking-wider w-10 text-right"
        style={{ color: GREY_TEXT }}
      >
        {label}
      </span>
      <div
        className="flex-1 h-1.5 rounded-full"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span
        className="font-mono text-[9px] tabular-nums w-6"
        style={{ color: GREY_TEXT }}
      >
        {Math.round(value)}
      </span>
    </div>
  );
}

// ─── Session type icons / colors ─────────────────────────────────────────────
function sessionChipStyle(label: string): React.CSSProperties {
  const l = label.toLowerCase();
  if (l.includes('long'))
    return {
      background: `rgba(${TEAL}, 0.08)`,
      border: `1px solid rgba(${TEAL}, 0.2)`,
      color: `rgba(${TEAL}, 0.8)`,
    };
  if (l.includes('tempo') || l.includes('threshold'))
    return {
      background: `rgba(${AMBER}, 0.08)`,
      border: `1px solid rgba(${AMBER}, 0.2)`,
      color: `rgba(${AMBER}, 0.8)`,
    };
  if (l.includes('interval') || l.includes('vo2') || l.includes('speed'))
    return {
      background: 'rgba(240, 100, 100, 0.08)',
      border: '1px solid rgba(240, 100, 100, 0.2)',
      color: 'rgba(240, 130, 130, 0.85)',
    };
  if (l.includes('rest') || l.includes('recovery') || l.includes('easy'))
    return {
      background: 'rgba(100,116,139,0.06)',
      border: '1px solid rgba(100,116,139,0.12)',
      color: 'rgba(100,116,139,0.6)',
    };
  return {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    color: GREY_TEXT,
  };
}

// ─── Week block (READY state) ─────────────────────────────────────────────────
interface WeekBlockProps {
  week: {
    week_index: number;
    date_range: string;
    status: 'completed' | 'current' | 'upcoming';
    coach_summary: string;
    key_sessions: string[];
    flags: ('fatigue' | 'missed_sessions')[];
  };
  isCurrentPhase: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function WeekBlock({
  week,
  isCurrentPhase,
  isExpanded,
  onToggle,
}: WeekBlockProps) {
  const isCurrent = week.status === 'current';
  const isCompleted = week.status === 'completed';
  const isRestWeek = week.key_sessions.some(
    s =>
      s.toLowerCase().includes('rest') || s.toLowerCase().includes('recovery')
  );

  const nodeColor = isCurrent
    ? `rgba(${TEAL}, 1)`
    : isCompleted
      ? `rgba(${TEAL}, 0.35)`
      : `rgba(${AMBER}, 0.25)`;
  const nodeRing = isCurrent
    ? `rgba(${TEAL}, 0.5)`
    : isCompleted
      ? `rgba(${TEAL}, 0.15)`
      : 'rgba(255,255,255, 0.06)';

  return (
    <div
      className={cn('relative pb-3 min-h-[44px] rounded-lg px-3 py-2')}
      style={{
        background: isCurrent ? 'rgba(14,22,32,0.8)' : 'transparent',
        border: isCurrent
          ? `1px solid rgba(${TEAL}, 0.2)`
          : '1px solid transparent',
        boxShadow: isCurrent
          ? `inset 0 0 20px rgba(${TEAL}, 0.03)`
          : 'none',
      }}
    >
      {/* Timeline node */}
      <div
        className="absolute z-10 flex items-center justify-center"
        style={{ left: '-24px', top: '10px', width: '18px', height: '18px' }}
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{ border: `1.5px solid ${nodeRing}` }}
        />
        <div
          className="rounded-full"
          style={{
            width: isCurrent ? '7px' : '5px',
            height: isCurrent ? '7px' : '5px',
            backgroundColor: nodeColor,
          }}
        />
        {isCurrent && (
          <div
            className="absolute inset-[-3px] rounded-full"
            style={{ boxShadow: `0 0 10px rgba(${TEAL}, 0.3)` }}
          />
        )}
      </div>

      {/* Header row — always visible */}
      <button
        className="w-full text-left"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            className="font-mono text-[12px] tabular-nums font-medium"
            style={{ color: isCurrent ? WHITE_TEXT : GREY_TEXT }}
          >
            W{week.week_index}
          </span>
          <span
            className="font-mono text-[10px]"
            style={{ color: 'rgba(160,160,160,0.5)' }}
          >
            {week.date_range}
          </span>
          <span
            className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{
              background: isCurrent
                ? `rgba(${TEAL}, 0.1)`
                : isCompleted
                  ? 'rgba(255,255,255,0.04)'
                  : `rgba(${AMBER}, 0.06)`,
              color: isCurrent
                ? `rgba(${TEAL}, 0.8)`
                : isCompleted
                  ? GREY_TEXT
                  : `rgba(${AMBER}, 0.6)`,
              border: `1px solid ${
                isCurrent
                  ? `rgba(${TEAL}, 0.2)`
                  : isCompleted
                    ? 'rgba(255,255,255,0.06)'
                    : `rgba(${AMBER}, 0.12)`
              }`,
            }}
          >
            {isCompleted ? 'DONE' : isCurrent ? 'LIVE' : 'NEXT'}
          </span>
          {isRestWeek && (
            <span
              className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded flex items-center gap-1"
              style={{
                background: 'rgba(100,116,139,0.08)',
                color: 'rgba(100,116,139,0.6)',
                border: '1px solid rgba(100,116,139,0.12)',
              }}
            >
              <Shield className="h-2.5 w-2.5" />
              REST
            </span>
          )}
          {week.flags.length > 0 && (
            <Badge
              variant="destructive"
              className="text-[9px] font-mono h-4 px-1.5"
            >
              <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
              {week.flags.includes('fatigue') ? 'FATIGUE' : 'MISSED'}
            </Badge>
          )}
          {/* Expand chevron */}
          <span className="ml-auto" style={{ color: GREY_TEXT }}>
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </span>
        </div>

        {/* Coach summary — always visible */}
        <p
          className="font-mono text-[11px] leading-relaxed mb-2"
          style={{ color: GREY_TEXT, fontStyle: 'italic' }}
        >
          "{week.coach_summary}"
        </p>
      </button>

      {/* Load bar */}
      <div className="mb-2">
        <LoadBar
          value={week.key_sessions.length * 25}
          max={100}
          label="Load"
        />
      </div>

      {/* Session chips — always visible summary */}
      {!isExpanded && week.key_sessions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {week.key_sessions.map((session, idx) => (
            <span
              key={idx}
              className="font-mono text-[9px] px-2 py-0.5 rounded"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                color: GREY_TEXT,
              }}
            >
              {session}
            </span>
          ))}
        </div>
      )}

      {/* Expanded drill-down */}
      {isExpanded && week.key_sessions.length > 0 && (
        <div className="space-y-1.5 mt-1">
          <p
            className="font-mono text-[9px] uppercase tracking-wider mb-1.5"
            style={{ color: 'rgba(160,160,160,0.4)' }}
          >
            Sessions
          </p>
          {week.key_sessions.map((session, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: `rgba(${TEAL}, 0.5)` }}
              />
              <span
                className="font-mono text-[11px] flex-1"
                style={sessionChipStyle(session)}
              >
                {session}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export function SeasonView({ currentDate: _currentDate }: SeasonViewProps) {
  useAuth();
  const queryClient = useQueryClient();

  // Local UI state
  const [localPhase, setLocalPhase] = useState<LocalPhase>('idle');
  const [planResponse, setPlanResponse] = useState<CoachChatResponse | null>(null);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set());
  const [simulatedStage, setSimulatedStage] = useState<PlanBuildStageValue>(
    PlanBuildStage.ANALYZE_RECENT_TRAINING
  );

  // Fetch season summary; poll while building
  const { data: response, isLoading } = useAuthenticatedQuery({
    queryKey: ['seasonSummary'],
    queryFn: () => fetchSeasonSummary(),
    retry: 1,
    refetchInterval: localPhase === 'building' ? 8000 : false,
  });

  // Fetch active plan instance for summary fallback
  const { data: instancesData } = usePlanInstances({ status: 'active' });
  const activeInstanceId = useMemo(() => {
    const items = instancesData?.items ?? [];
    return items.length > 0 ? items[0].id : null;
  }, [instancesData]);

  // Simulate build stage progression while waiting for the HTTP call
  useEffect(() => {
    if (localPhase !== 'building') {
      setSimulatedStage(PlanBuildStage.ANALYZE_RECENT_TRAINING);
      return;
    }
    let idx = 0;
    setSimulatedStage(BUILD_STAGES[0]);
    const interval = setInterval(() => {
      idx = Math.min(idx + 1, BUILD_STAGES.length - 1);
      setSimulatedStage(BUILD_STAGES[idx]);
      if (idx === BUILD_STAGES.length - 1) clearInterval(interval);
    }, 6000);
    return () => clearInterval(interval);
  }, [localPhase]);

  // ─── Action handlers ─────────────────────────────────────────────────────
  const handleGenerate = async (message: string) => {
    log.debug('SEASON', 'Starting plan generation:', message);
    setLocalPhase('building');
    try {
      const result = await sendCoachChat(message, {
        message_id: crypto.randomUUID(),
      });
      log.debug('SEASON', 'Plan generation complete:', result.response_type);
      setPlanResponse(result);
      setLocalPhase('review');
    } catch (error) {
      log.error('SEASON', 'Plan generation failed:', error);
      setLocalPhase('idle');
    }
  };

  const handleConfirm = async () => {
    log.debug('SEASON', 'Confirming plan to calendar');
    try {
      await sendCoachChat('yes', {
        message_id: crypto.randomUUID(),
      });
      // Track first plan generation
      try {
        const PH_KEY = "ph_first_plan_generated";
        if (!localStorage.getItem(PH_KEY)) {
          localStorage.setItem(PH_KEY, "1");
          trackFirstPlanGenerated({
            weeks: planResponse?.planned_weeks?.length ?? 0,
            sessions: planResponse?.plan_items?.length ?? 0,
            goal_type: planResponse?.response_type,
          });
        }
      } catch { /* analytics should never break UX */ }

      setLocalPhase('idle');
      setPlanResponse(null);
      queryClient.invalidateQueries({ queryKey: ['seasonSummary'] });
      queryClient.invalidateQueries({ queryKey: ['calendarRange'] });
      // Invalidate Schedule page data so new planned sessions appear
      queryClient.invalidateQueries({ queryKey: ['calendarWeek'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendar', 'month'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendarToday'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendarSeason'], exact: false });
    } catch (error) {
      log.error('SEASON', 'Failed to apply plan:', error);
      toast({
        title: 'Failed to apply plan',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAdjust = () => {
    setLocalPhase('idle');
    setPlanResponse(null);
  };

  const toggleWeek = (weekIndex: number) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekIndex)) next.delete(weekIndex);
      else next.add(weekIndex);
      return next;
    });
  };

  // ─── Shared glass card style ─────────────────────────────────────────────
  const glassCard: React.CSSProperties = {
    background: CARD_BG,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${BORDER_INACTIVE}`,
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.03), inset 0 0 20px rgba(0,0,0,0.2)',
  };

  // ─── Local phase overrides ───────────────────────────────────────────────
  // Building — show running man + progress card
  if (localPhase === 'building') {
    const outboundProgress: PlanBuildProgress = {
      status: 'IN_PROGRESS',
      stage: simulatedStage,
      started_at: new Date().toISOString(),
    };
    return (
      <>
        <div
          className="rounded-xl p-6"
          style={glassCard}
          role="region"
          aria-label="Generating season plan"
        >
          <div className="flex flex-col items-center gap-6">
            <RunnerProcessingIndicator
              caption="Building your plan…"
              speedMultiplier={1.2}
            />
            <PlanBuildProgressCard outboundProgress={outboundProgress} />
          </div>
        </div>
        {/* Preflight is closed while building */}
        <SeasonPreflightDialog
          open={false}
          onOpenChange={() => {}}
          onGenerate={handleGenerate}
        />
      </>
    );
  }

  // Review — show plan summary before committing
  if (localPhase === 'review' && planResponse) {
    return (
      <>
        <div className="rounded-xl p-6" style={glassCard}>
          <SeasonPlanReview
            response={planResponse}
            onConfirm={handleConfirm}
            onAdjust={handleAdjust}
          />
        </div>
        <SeasonPreflightDialog
          open={false}
          onOpenChange={() => {}}
          onGenerate={handleGenerate}
        />
      </>
    );
  }

  // ─── Backend-driven states ───────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-12 rounded-xl"
        style={glassCard}
      >
        <Loader2
          className="h-5 w-5 animate-spin mr-2"
          style={{ color: GREY_TEXT }}
        />
        <span className="font-mono text-sm" style={{ color: GREY_TEXT }}>
          Loading season narrative...
        </span>
      </div>
    );
  }

  if (!response) {
    return <SeasonSummaryCard instanceId={activeInstanceId} />;
  }

  const { status, season, narrative, reason, next_steps, generating } = response;

  // GENERATING (backend) — poll until READY
  if (status === 'GENERATING') {
    const backendProgress: PlanBuildProgress | null = generating?.stage
      ? {
          status: 'IN_PROGRESS',
          stage: (generating.stage as PlanBuildStageValue) ?? PlanBuildStage.GENERATE_TRAINING_PLAN,
          started_at: new Date().toISOString(),
        }
      : null;

    return (
      <div
        className="rounded-xl p-6"
        style={glassCard}
        role="region"
        aria-label="Generating season plan"
      >
        <div className="flex flex-col items-center gap-6">
          <RunnerProcessingIndicator
            caption="Building your plan…"
            speedMultiplier={1.2}
          />
          <PlanBuildProgressCard outboundProgress={backendProgress} />
          {reason && (
            <p
              className="font-mono text-sm mt-4"
              style={{ color: GREY_TEXT }}
            >
              {reason}
            </p>
          )}
        </div>
      </div>
    );
  }

  // LEARNING — not enough data
  if (status === 'LEARNING') {
    return (
      <div
        className="flex flex-col justify-center py-12 px-6 rounded-xl"
        style={glassCard}
      >
        <h2
          className="text-xl font-semibold mb-2"
          style={{ color: WHITE_TEXT }}
        >
          Season
        </h2>
        <p className="font-mono text-sm" style={{ color: GREY_TEXT }}>
          {reason ?? 'The coach needs more training data to build a plan.'}
        </p>
        {next_steps.length > 0 && (
          <ul
            className="list-disc list-inside font-mono text-sm mt-4 space-y-1"
            style={{ color: GREY_TEXT }}
          >
            {next_steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // AUTO_READY — baseline available, plan not yet built
  if (status === 'AUTO_READY' && narrative && !isFullNarrative(narrative)) {
    const baseline = narrative as BaselinePlanArtifact;
    return (
      <>
        <div className="h-full flex flex-col overflow-y-auto">
          <div className="flex-shrink-0 mb-4">
            <h2
              className="text-xl font-semibold mb-1"
              style={{ color: WHITE_TEXT }}
            >
              Season
            </h2>
            <p className="font-mono text-sm" style={{ color: GREY_TEXT }}>
              {season}. {reason ?? 'Baseline view available.'}
            </p>
          </div>

          <div
            className="p-5 rounded-xl mb-4"
            style={{
              ...glassCard,
              border: `1px solid rgba(${TEAL}, 0.18)`,
            }}
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <Badge
                  variant="secondary"
                  className="mb-2 font-mono text-[9px]"
                >
                  Baseline
                </Badge>
                <h3
                  className="font-semibold text-base"
                  style={{ color: WHITE_TEXT }}
                >
                  Focus: {baseline.current_focus}
                </h3>
              </div>
            </div>

            <p
              className="font-mono text-sm mb-3"
              style={{ color: GREY_TEXT }}
            >
              {baseline.summary}
            </p>

            {baseline.weekly_structure.length > 0 && (
              <ul
                className="font-mono text-sm list-disc list-inside space-y-1 mb-3"
                style={{ color: GREY_TEXT }}
              >
                {baseline.weekly_structure.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}

            <p
              className="font-mono text-xs mb-5"
              style={{ color: GREY_TEXT }}
            >
              {baseline.note}
            </p>

            <Button
              className="w-full sm:w-auto font-mono text-xs"
              onClick={() => setLocalPhase('preflight')}
              style={{
                background: `rgba(${TEAL}, 0.12)`,
                border: `1px solid rgba(${TEAL}, 0.3)`,
                color: `rgba(${TEAL}, 0.95)`,
              }}
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Build my season plan
            </Button>
          </div>
        </div>

        {/* Preflight dialog — mounts on top */}
        <SeasonPreflightDialog
          open={localPhase === 'preflight'}
          onOpenChange={open => !open && setLocalPhase('idle')}
          onGenerate={handleGenerate}
        />
      </>
    );
  }

  // READY — full plan with phase timeline and week drill-down
  if (status === 'READY' && narrative && isFullNarrative(narrative)) {
    const seasonSummary = narrative;
    return (
      <>
        <div className="h-full flex flex-col overflow-y-auto scrollbar-hide">
          {/* Header */}
          <div className="flex-shrink-0 mb-4">
            <div className="flex items-center justify-between mb-1">
              <h2
                className="text-xl font-semibold"
                style={{ color: WHITE_TEXT }}
              >
                Season
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLocalPhase('preflight')}
                className="font-mono text-[9px] uppercase tracking-wider text-slate-500 hover:text-slate-300"
              >
                Rebuild
              </Button>
            </div>
            <p className="font-mono text-sm" style={{ color: GREY_TEXT }}>
              Phase timeline — how your plan unfolds.
            </p>
          </div>

          {/* Load arc chart */}
          {seasonSummary.phases.length > 0 && (
            <div className="mb-4 px-1">
              <p
                className="font-mono text-[9px] uppercase tracking-wider mb-2"
                style={{ color: 'rgba(160,160,160,0.4)' }}
              >
                Season Load Arc
              </p>
              <SeasonLoadChart phases={seasonSummary.phases} />
            </div>
          )}

          {/* Goal race */}
          {seasonSummary.goal_race && (
            <div className="mb-4 p-4 rounded-xl" style={glassCard}>
              <h3
                className="font-medium mb-1"
                style={{ color: WHITE_TEXT }}
              >
                Goal Race
              </h3>
              <p className="font-mono text-sm" style={{ color: GREY_TEXT }}>
                {seasonSummary.goal_race.name} ·{' '}
                {seasonSummary.goal_race.weeks_to_race} weeks remaining
              </p>
            </div>
          )}

          {/* Phase timeline */}
          <div className="flex-1 space-y-6">
            {seasonSummary.phases.map((phase, phaseIndex) => {
              const isCurrentPhase =
                phase.name === seasonSummary.current_phase;
              return (
                <div key={phaseIndex} className="relative">
                  {/* Phase header */}
                  <div
                    className="mb-3 pb-2"
                    style={{
                      borderBottom: `1px solid ${
                        isCurrentPhase
                          ? `rgba(${TEAL}, 0.2)`
                          : BORDER_INACTIVE
                      }`,
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <h3
                        className="text-base font-mono font-medium tracking-tight uppercase"
                        style={{
                          color: isCurrentPhase
                            ? `rgba(${TEAL}, 0.9)`
                            : WHITE_TEXT,
                        }}
                      >
                        {phase.name.toUpperCase()}
                      </h3>
                      {isCurrentPhase && (
                        <Badge
                          variant="default"
                          className="text-[9px] font-mono"
                          style={{
                            background: `rgba(${TEAL}, 0.15)`,
                            color: `rgba(${TEAL}, 0.9)`,
                            border: `1px solid rgba(${TEAL}, 0.3)`,
                          }}
                        >
                          Current
                        </Badge>
                      )}
                    </div>
                    <p
                      className="font-mono text-[11px] mt-1 uppercase tracking-wider"
                      style={{ color: GREY_TEXT }}
                    >
                      Intent: {phase.intent}
                    </p>
                  </div>

                  {/* Weeks with spine */}
                  <div className="relative ml-4 pl-6">
                    <div
                      className="absolute left-0 top-0 bottom-0"
                      style={{
                        width: '2px',
                        background: isCurrentPhase
                          ? `linear-gradient(to bottom, rgba(${TEAL}, 0.5), rgba(${TEAL}, 0.15))`
                          : 'rgba(255,255,255, 0.06)',
                      }}
                    />
                    <div className="space-y-3">
                      {phase.weeks.map(week => (
                        <WeekBlock
                          key={week.week_index}
                          week={week}
                          isCurrentPhase={isCurrentPhase}
                          isExpanded={expandedWeeks.has(week.week_index)}
                          onToggle={() => toggleWeek(week.week_index)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preflight for rebuilding */}
        <SeasonPreflightDialog
          open={localPhase === 'preflight'}
          onOpenChange={open => !open && setLocalPhase('idle')}
          onGenerate={handleGenerate}
        />
      </>
    );
  }

  // Fallback
  return (
    <>
      <div
        className="flex items-center justify-center py-12 rounded-xl"
        style={glassCard}
      >
        <span className="font-mono text-sm" style={{ color: GREY_TEXT }}>
          Season view not available. {reason ?? 'Try again later.'}
        </span>
      </div>
      <SeasonPreflightDialog
        open={localPhase === 'preflight'}
        onOpenChange={open => !open && setLocalPhase('idle')}
        onGenerate={handleGenerate}
      />
    </>
  );
}
